import React from 'react';
import { Box, Typography } from '@mui/material';
import { MOBILE_DASHBOARD_MODES } from './mobileDashboardModes';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';
import { LOGIN_ACCENT } from '../auth/loginDarkTheme';

/**
 * Segmented pill switcher for mobile dashboard: Overview · Grid · Ultra · Calendar.
 */
export default function MobileDashboardViewSwitcher({ value = 'overview', onChange }) {
  const activeIndex = Math.max(
    0,
    MOBILE_DASHBOARD_MODES.findIndex((mode) => mode.id === value),
  );
  const segmentWidth = 100 / MOBILE_DASHBOARD_MODES.length;

  return (
    <Box
      role="tablist"
      aria-label="Dashboard view"
      sx={{
        ...mobileAppGlassCard,
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        p: 0.45,
        borderRadius: '18px',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: `calc(${activeIndex * segmentWidth}% + 4px)`,
          width: `calc(${segmentWidth}% - 8px)`,
          borderRadius: '14px',
          background: `linear-gradient(135deg, ${LOGIN_ACCENT} 0%, #3ab896 100%)`,
          boxShadow: '0 8px 20px rgba(76, 225, 182, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
          transition: 'left 0.32s cubic-bezier(0.34, 1.2, 0.64, 1)',
          pointerEvents: 'none',
        }}
      />

      {MOBILE_DASHBOARD_MODES.map((mode) => {
        const Icon = mode.icon;
        const selected = value === mode.id;
        return (
          <Box
            key={mode.id}
            component="button"
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange?.(mode.id)}
            sx={{
              flex: 1,
              zIndex: 1,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.2,
              py: 0.75,
              px: 0.25,
              minWidth: 0,
              WebkitTapHighlightColor: 'transparent',
              transition: 'transform 0.15s ease',
              '&:active': { transform: 'scale(0.97)' },
            }}
          >
            <Icon
              sx={{
                fontSize: 18,
                color: selected ? '#031912' : 'rgba(148, 163, 184, 0.92)',
                transition: 'color 0.2s ease',
              }}
            />
            <Typography
              sx={{
                fontSize: '0.62rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: selected ? '#031912' : 'rgba(148, 163, 184, 0.88)',
                lineHeight: 1.1,
                transition: 'color 0.2s ease',
              }}
            >
              {mode.shortLabel}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
